import { Dispatch } from 'redux';
import { getEmployerChangeLogs } from '../../global/ducks/benefit-group-change-log-api';
import { getFormattedDateYmd } from '../../global/helpers/date-helpers';
import { KeyValuePair } from '../../pages/arc/models/arc-epi-props';
import { mapEventType } from '../change-log/change-log-requests';
import { BenefitGroupChangeLogApiFilter, BenefitGroupChangeLogApiResponse, BenefitGroupChangeLogItem, BenefitGroupChangeLogPayload } from '../models/benefit-group-changelog-models';
import { BackendSearchState, FilterOptions, FilterTypes, SearchApp } from '../models/search-models';
import { defaultBackendPageSize, setBeginRequest, setEndRequest, setFilterOptions, setList, setPaginationInfo } from '../slices/backend-search-slice';

function formatName(firstName: string, lastName: string) : string {
	if (firstName && lastName){
		return `${lastName}, ${firstName}`;
	}

	if (lastName){
		return lastName;
	}

	if (firstName){
		return firstName;
	}

	return '';
}

function convertEventLogToBenefitGroupChange(item: any) : BenefitGroupChangeLogItem {
	return {
		submittedDate: getFormattedDateYmd(item?.created, true),
		lastActivityDate: getFormattedDateYmd(item?.change_time, true),
		status: `${item?.change_state} / ${item?.change_sub_status}`,
		eventType: item?.summary,
		requestedBy: formatName(item?.requestor_user_first_name, item?.requestor_user_last_name),
		completed: formatName(item?.completor_user_first_name, item?.completor_user_last_name),
		changeMadeFor: item?.entity,
		changeType: item?.change_type,
		id: item?.change_id,		
		requestorUserId: item?.requestor_user_id,
		requestorFirstName: item?.requestor_user_first_name,
		requestorLastName: item?.requestor_user_last_name,
		requestorEmail: item?.requestor_user_email_address,
		changeMadeForCn: item?.entity_pin_id,
		requestorUserPhoneNumber: item?.requestor_user_phone_number,
		requestorUserPhoneCountryCode: item?.requestor_user_phone_country_code,
		requestorUserPhoneAreaCode: item?.requestor_user_phone_area_code,
		requestorUserPhoneExtension: item?.requestor_user_phone_extension,		
		change_time: item?.change_time,
	};
}

function mapFilterType(itemValue): string {
	switch (itemValue) {
		case 'submittedDate':
			return 'submitted_date';

		case 'lastActivityDate':
			return 'last_updated_date';

		case 'status':
			return 'status';

		case 'eventType':
			return 'event_type';

		case 'requestBy':
			return 'requested_by';

		case 'completed':
			return 'completed_by';

		case 'changeMadeFor':
			return 'entity';

		case 'changeType':
			return 'change_type';
	
		default:
			return itemValue;			
	}
}

function createFilterPayload(searchState: BackendSearchState) {
	const filterBy: BenefitGroupChangeLogApiFilter = {};

	const filter: BenefitGroupChangeLogPayload = {
		page: searchState?.currentPage?.toString(),
		limit: searchState?.pageSize?.toString() || `${defaultBackendPageSize}`,
		filter_by: filterBy,
	};
	
	const eventType = searchState?.seletedFilters.filter(f => f.type === FilterTypes.eventType);
	const changeMadeFor = searchState?.seletedFilters.filter(f => f.type === FilterTypes.changeMadeFor);
	const requestedBy = searchState?.seletedFilters.filter(f => f.type === FilterTypes.requestBy);
	const status = searchState?.seletedFilters.filter(f => f.type === FilterTypes.status);
	const changeType = searchState?.seletedFilters.filter(f => f.type === FilterTypes.changeType);

	const from = searchState?.seletedFilters.filter(f => f.type === FilterTypes.from);
	const to = searchState?.seletedFilters.filter(f => f.type === FilterTypes.to);

	const sort = searchState?.sorts[0];
	if (from[0]?.value || to[0]?.value) {		

		const range : any = {};

		if (from[0]?.value){
			range.from_date = getFormattedDateYmd(from[0]?.value?.split(':')[1], true);
		}

		if (to[0]?.value){
			range.to_date = getFormattedDateYmd(to[0]?.value?.split(':')[1], true);
		}		

		filterBy.date_range = range;
	}	

	if (eventType?.length > 0) {
		filterBy.event_type = eventType?.map(e => e.value);
	}

	if (changeMadeFor?.length > 0) {
		filterBy.entities = changeMadeFor?.map(e => e.value);
	}

	if (requestedBy?.length > 0) {
		filterBy.requested_by = requestedBy?.map(e => e.value);
	}

	if (status?.length > 0) {
		filterBy.status = status?.map(e => e.value);
	}

	if (changeType?.length > 0) {
		filterBy.change_type = changeType?.map(e => e.value);
	}

	if (sort) {
		filter.sort_by = {
			field: mapFilterType(sort.type), 
			order_by: sort.sortBy,
		};
	}
	return filter;
}

export function parseFilterOptions(response: BenefitGroupChangeLogApiResponse, mapList: KeyValuePair[]): { [key: string]: FilterOptions } {

	const filterOptions: any = {};
	 
	if (response?.distinct_filter?.entities) {
		filterOptions[FilterTypes.changeMadeFor] = {
			type: FilterTypes.changeMadeFor,
			options: response?.distinct_filter?.entities?.map(e => {
				return {
					key: FilterTypes.changeMadeFor,
					display: e,
					value: e,
				};
			}),
		};
	}

	if (response?.distinct_filter?.event_type) {
		filterOptions[FilterTypes.eventType] = {
			type: FilterTypes.eventType,
			options: response?.distinct_filter?.event_type?.map(e => {
				return {
					key: FilterTypes.eventType,
					display: mapEventType(e, mapList),
					value: e,
				};
			}),
		};
	}

	if (response?.distinct_filter?.requested_by) {
		filterOptions[FilterTypes.requestBy] = {
			type: FilterTypes.requestBy,
			options: response?.distinct_filter?.requested_by?.map(e => {
				return {
					key: FilterTypes.requestBy,
					display: formatName(e?.requestor_user_first_name, e?.requestor_user_last_name),
					value: `${e?.requestor_user_id}`,
				};
			}),
		};
	}

	if (response?.distinct_filter?.status) {
		filterOptions[FilterTypes.status] = {
			type: FilterTypes.status,
			options: response?.distinct_filter?.status?.map(e => {
				return {
					key: FilterTypes.status,
					display: e,
					value: e,
				};
			}),
		};
	}

	if (response?.distinct_filter?.change_type) {
		filterOptions[FilterTypes.changeType] = {
			type: FilterTypes.changeType,
			options: response?.distinct_filter?.change_type?.map(e => {
				return {
					key: FilterTypes.changeType,
					display: e,
					value: e,
				};
			}),
		};
	}

	return filterOptions;
}

export function handleEmployeeChangeLogResponse(response: BenefitGroupChangeLogApiResponse, mapList: KeyValuePair[], dispatch: Dispatch<any>) {

	dispatch(setFilterOptions(parseFilterOptions(response, mapList), SearchApp.BENEFIT_GROUP_CHANGE_LOG));
	dispatch(setList(response.eventLogs.map(ev=> convertEventLogToBenefitGroupChange(ev)), SearchApp.BENEFIT_GROUP_CHANGE_LOG));
	dispatch(setPaginationInfo(response?.pagination?.totalPages, response?.pagination?.totalRecordsCount, response?.pagination?.pageSize, SearchApp.BENEFIT_GROUP_CHANGE_LOG));
}

export function updateBenefitGroupChangeLogList(searchState: BackendSearchState, mapList: KeyValuePair[], dispatch: Dispatch<any>, groupId: number) {	
	const app = SearchApp.BENEFIT_GROUP_CHANGE_LOG;
	dispatch(setList(searchState.list, app));
	if (groupId) {
		dispatch(setBeginRequest(app));
		dispatch(getEmployerChangeLogs(
			groupId,
			createFilterPayload(searchState),
			(response) => handleEmployeeChangeLogResponse(response?.data, mapList, dispatch),
			() => setEndRequest(app),
		));
	}
}
